import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Moon, Sun, Monitor, Download, CloudOff, MessageSquare } from 'lucide-react';
import { CustomSelect } from './controls/CustomSelect';

import './Header.css';

import { useAppStore } from '../store/useAppStore';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => void;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

import type { ToolTab } from './Layout';

export const Header: React.FC<{ activeTab: ToolTab, onTabChange: (tab: ToolTab) => void }> = ({ activeTab, onTabChange }) => {
  const { t, i18n } = useTranslation('common');
  const theme = useAppStore((state) => state.theme);
  const setTheme = useAppStore((state) => state.setTheme);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [showInstall, setShowInstall] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    const handleOnline = () => { setIsOffline(false); };
    const handleOffline = () => { setIsOffline(true); };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      const dismissed = localStorage.getItem('MamaPDF-install-dismissed');
      if (dismissed) {
        const time = parseInt(dismissed, 10);
        if (Date.now() - time < 30 * 24 * 60 * 60 * 1000) return;
      }
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setShowInstall(true);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'dismissed') {
      localStorage.setItem('MamaPDF-install-dismissed', Date.now().toString());
    }
    setDeferredPrompt(null);
    setShowInstall(false);
  };

  const handleThemeChange = (value: string) => {
    setTheme(value);
  };

  const handleLanguageChange = (value: string) => {
    void i18n.changeLanguage(value);
  };

  return (
    <header className="header">
      <div className="header-left">
        <div className="flex-row-center-gap-8">
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 256 256" fill="none" stroke="currentColor" strokeWidth="16" strokeLinecap="round" strokeLinejoin="round">
            <path d="M 104 72 V 40 H 160 L 208 88 V 184 H 152" />
            <path d="M 160 40 V 88 H 208" />
            <path d="M 48 72 H 104 L 152 120 V 216 H 48 Z" />
            <path d="M 104 72 V 120 H 152" />
          </svg>
          <h1 className="logo m-0">
            <span className="sr-only">{t('header.srTitle', 'Free Client-Side PDF Toolset - ')}</span>
            {t('header.brandName', 'MamaPDF')}
          </h1>
        </div>
      </div>

      <div className="header-nav">
        <div className="tab-bar">
          <button className={`tab-btn ${activeTab === 'merge' ? 'active' : ''}`} onClick={() => { onTabChange('merge'); }}>
            {t('tabs.merge', 'Merge')}
          </button>
          <button className={`tab-btn ${activeTab === 'split' ? 'active' : ''}`} onClick={() => { onTabChange('split'); }}>
            {t('tabs.split', 'Split')}
          </button>
          <button className={`tab-btn ${activeTab === 'rotate' ? 'active' : ''}`} onClick={() => { onTabChange('rotate'); }}>
            {t('tabs.rotate', 'Rotate')}
          </button>
          <button className={`tab-btn ${activeTab === 'watermark' ? 'active' : ''}`} onClick={() => { onTabChange('watermark'); }}>
            {t('tabs.watermark', 'Watermark')}
          </button>
          <button className={`tab-btn ${activeTab === 'protect' ? 'active' : ''}`} onClick={() => { onTabChange('protect'); }}>
            {t('tabs.protect', 'Protect')}
          </button>
          <button className={`tab-btn ${activeTab === 'jpgToPdf' ? 'active' : ''}`} onClick={() => { onTabChange('jpgToPdf'); }}>
            {t('tabs.jpgToPdf', 'JPG to PDF')}
          </button>
          <button className={`tab-btn ${activeTab === 'pageNumbers' ? 'active' : ''}`} onClick={() => { onTabChange('pageNumbers'); }}>
            {t('tabs.pageNumbers', 'Page Numbers')}
          </button>
          <button className={`tab-btn ${activeTab === 'crop' ? 'active' : ''}`} onClick={() => { onTabChange('crop'); }}>
            {t('tabs.crop', 'Crop')}
          </button>
          <button className={`tab-btn ${activeTab === 'repair' ? 'active' : ''}`} onClick={() => { onTabChange('repair'); }}>
            {t('tabs.repair', 'Repair')}
          </button>
          <button className={`tab-btn ${activeTab === 'base64' ? 'active' : ''}`} onClick={() => { onTabChange('base64'); }}>
            {t('tabs.base64', 'Base64')}
          </button>
          <button className={`tab-btn ${activeTab === 'pdfToJpg' ? 'active' : ''}`} onClick={() => { onTabChange('pdfToJpg'); }}>
            {t('tabs.pdfToJpg', 'PDF to JPG')}
          </button>
          <button className={`tab-btn ${activeTab === 'htmlToPdf' ? 'active' : ''}`} onClick={() => { onTabChange('htmlToPdf'); }}>
            {t('tabs.htmlToPdf', 'HTML to PDF')}
          </button>
          <button className={`tab-btn ${activeTab === 'markdown' ? 'active' : ''}`} onClick={() => { onTabChange('markdown'); }}>
            {t('tabs.markdown', 'To Markdown')}
          </button>
          <button className={`tab-btn ${activeTab === 'sanitize' ? 'active' : ''}`} onClick={() => { onTabChange('sanitize'); }}>
            {t('tabs.sanitize', 'Sanitize')}
          </button>
        </div>
      </div>


      <div className="header-right">
        {isOffline && (
          <div className="offline-indicator">
            <CloudOff size={14} />
            <span>{t('header.offline', 'Offline')}</span>
          </div>
        )}
        
        {showInstall && (
          <button className="install-btn" onClick={() => { void handleInstall(); }}>
            <Download size={16} />
            <span className="install-label">{t('header.install', 'Install')}</span>
          </button>
        )}

        <a 
          href="https://github.com/mama-tools/mama-tools-support/issues/new/choose" 
          target="_blank" 
          rel="noopener noreferrer"
          className="header-icon-btn"
          aria-label={t('header.feedback', 'Feedback & Support')}
          title={t('header.feedback', 'Feedback & Support')}
        >
          <MessageSquare size={16} />
        </a>

        <CustomSelect
          value={i18n.language.split('-')[0]}
          onChange={handleLanguageChange}
          icon={<Globe size={16} />}
          ariaLabel={t('header.language', 'Language')}
          options={[
            { value: 'en', label: 'English' },
            { value: 'ar', label: 'العربية' },
          ]}
        />

        <CustomSelect
          value={theme}
          onChange={handleThemeChange}
          icon={theme === 'system' ? <Monitor size={16} /> : theme === 'dark' ? <Moon size={16} /> : <Sun size={16} />}
          ariaLabel={t('header.theme', 'Theme')}
          options={[
            { value: 'system', label: t('theme.system', 'System') },
            { value: 'light', label: t('theme.light', 'Light') },
            { value: 'dark', label: t('theme.dark', 'Dark') },
          ]}
        />
      </div>
    </header>
  );
};
